import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ActionToolbar from '../ActionToolbar/ActionToolbar';
import MessageMarkdown from './MessageMarkdown';
import TableOfContents, { getH2Headings } from '../ActionToolbar/TableOfContents';

export default function AssistantMessage({
  message,
  messages,
  isDarkMode,
  chatSessionId = '',
  formattedTime,
  isAI,
  isSystem,
}) {
  const headings = useMemo(
    () => getH2Headings(message?.content),
    [message?.content],
  );

  const showToc = headings?.length >= 3;

  const showFooter =
    (isAI || isSystem) &&
    message?.content.trim() !==
      'Subscription limit reached for Chatbot Queries. Please upgrade your plan.';

  return (
    <motion.div
      key={message?.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className={`w-full overflow-visible rounded-xl border shadow-sm ${
        isDarkMode ? 'border-gray-700 bg-[#1f2937]' : 'border-gray-200 bg-white'
      }`}
    >
      <div
        className={`flex items-center justify-between rounded-t-xl border-b px-4 py-3 ${
          isDarkMode
            ? 'border-indigo-800 bg-indigo-900'
            : 'border-indigo-100 bg-indigo-50'
        }`}
      >
        <div className="flex items-center gap-2 text-sm">
          <span
            className={`font-semibold ${
              isDarkMode ? 'text-indigo-200' : 'text-indigo-900'
            }`}
          >
            Ulegal
          </span>

          {formattedTime && (
            <span
              className={isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}
            >
              • {formattedTime}
            </span>
          )}
        </div>

        <ActionToolbar
          messageContent={message.content}
          transcript={messages}
          isDarkMode={isDarkMode}
          memoryId={message.memory_id || ''}
          chatSessionId={chatSessionId}
          isResearch={message?.source === 'Deep Research'}
        />
      </div>

      <div className="flex gap-6 px-6 py-5">
        <div
          className={`flex-1 min-w-0 text-sm leading-relaxed ${
            isDarkMode ? 'text-gray-200' : 'text-gray-800'
          }`}
        >
          <MessageMarkdown content={message.content} isDarkMode={isDarkMode} />

          {showFooter && (
            <AnimatePresence initial={false}>
              <motion.div
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="mt-4 flex justify-start"
              />
            </AnimatePresence>
          )}
        </div>

        {showToc && (
          <TableOfContents headings={headings} isDarkMode={isDarkMode} />
        )}
      </div>
    </motion.div>
  );
}
